import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Download, FileText, Trash2, Upload } from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { config } from '@/core/config'
import { showDeleteConfirm, showError, showSuccess } from '@/lib/swal'
import { entityDocumentService } from '@/services/entityDocumentService'
import { useAuthStore } from '@/store/auth.store'
import type { EntityDocument } from '@/api/types'

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024

function fileNameWithoutExtension(fileName: string): string {
  return fileName.replace(/\.[^.]+$/, '').trim()
}

function resolveDocumentPath(pathOrUrl: string | null | undefined): string {
  const raw = String(pathOrUrl ?? '').trim()
  if (!raw) return ''
  if (/^https?:\/\//i.test(raw)) return encodeURI(raw)
  if (/^data:/i.test(raw)) return raw
  const normalizedPath = raw.replace(/\\/g, '/').replace(/^\/+/, '')
  const base = String(config.api.baseUrl || '').replace(/\/+$/, '')
  if (!base) return encodeURI(`/${normalizedPath}`)
  return encodeURI(`${base}/${normalizedPath}`)
}

function formatDate(value?: string): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleDateString()
}

type PendingDocument = {
  localId: string
  file: File
  documentName: string
}

export type EntityDocumentsHandle = {
  uploadPending: (tableRowId: number) => Promise<void>
}

interface EntityDocumentsProps {
  tableName: string
  tableRowId: number
}

export const EntityDocuments = forwardRef<EntityDocumentsHandle, EntityDocumentsProps>(
  ({ tableName, tableRowId }, ref) => {
    const { t } = useTranslation('common')
    const { user } = useAuthStore()
    const branchId = user?.selectedBranch?.branchId

    const [documents, setDocuments] = useState<EntityDocument[]>([])
    const [pending, setPending] = useState<PendingDocument[]>([])
    const [loading, setLoading] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [documentName, setDocumentName] = useState('')
    const [file, setFile] = useState<File | null>(null)
    const [deletingId, setDeletingId] = useState<number | string | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const pendingRef = useRef<PendingDocument[]>([])
    const fileRef = useRef<File | null>(null)
    const documentNameRef = useRef('')

    pendingRef.current = pending
    fileRef.current = file
    documentNameRef.current = documentName

    const canSaveToServer = tableRowId > 0 && !!branchId

    const loadDocuments = useCallback(async () => {
      if (!tableName || tableRowId <= 0) return
      setLoading(true)
      try {
        const rows = await entityDocumentService.getByRef(tableName, tableRowId)
        setDocuments(rows)
      } catch (error: any) {
        showError(error?.message || t('entityDocuments.failedToLoad'))
      } finally {
        setLoading(false)
      }
    }, [tableName, tableRowId, t])

    useEffect(() => {
      void loadDocuments()
    }, [loadDocuments])

    const clearPicker = () => {
      setDocumentName('')
      setFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }

    const queuePending = (selected: File, name: string) => {
      setPending((prev) => [
        ...prev,
        {
          localId: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          file: selected,
          documentName: name,
        },
      ])
      clearPicker()
    }

    const uploadOne = async (selected: File, name: string, rowId: number) => {
      if (!branchId) throw new Error(t('entityDocuments.failedToUpload'))
      const formData = new FormData()
      formData.append('TableName', tableName)
      formData.append('TableRowId', String(rowId))
      formData.append('DocumentName', name)
      formData.append('BranchId', String(branchId))
      formData.append('file', selected)
      await entityDocumentService.addDocument(formData)
    }

    const handleFileChange = (selected: File | null) => {
      if (!selected) {
        setFile(null)
        return
      }
      if (selected.size > MAX_FILE_SIZE_BYTES) {
        showError(t('entityDocuments.fileTooLarge'))
        setFile(null)
        if (fileInputRef.current) fileInputRef.current.value = ''
        return
      }
      setFile(selected)
      setDocumentName(fileNameWithoutExtension(selected.name) || selected.name)
    }

    const handleUpload = async () => {
      if (!file) {
        showError(t('entityDocuments.pleaseSelectFile'))
        return
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        showError(t('entityDocuments.fileTooLarge'))
        return
      }
      const name = documentName.trim() || fileNameWithoutExtension(file.name) || file.name
      if (!canSaveToServer) {
        queuePending(file, name)
        return
      }

      setUploading(true)
      try {
        await uploadOne(file, name, tableRowId)
        clearPicker()
        await showSuccess(t('entityDocuments.uploadedSuccess'))
        await loadDocuments()
      } catch (error: any) {
        showError(error?.message || t('entityDocuments.failedToUpload'))
      } finally {
        setUploading(false)
      }
    }

    const handleDeleteSaved = async (doc: EntityDocument) => {
      if (!doc.id) return
      const confirmed = await showDeleteConfirm(doc.documentName || t('entityDocuments.document'))
      if (!confirmed) return
      setDeletingId(doc.id)
      try {
        await entityDocumentService.deleteDocument(doc.id)
        await showSuccess(t('entityDocuments.deletedSuccess'))
        await loadDocuments()
      } catch (error: any) {
        showError(error?.message || t('entityDocuments.failedToDelete'))
      } finally {
        setDeletingId(null)
      }
    }

    const handleDeletePending = async (item: PendingDocument) => {
      const confirmed = await showDeleteConfirm(item.documentName || t('entityDocuments.document'))
      if (!confirmed) return
      setPending((prev) => prev.filter((p) => p.localId !== item.localId))
    }

    useImperativeHandle(ref, () => ({
      uploadPending: async (rowId: number) => {
        if (!rowId || rowId <= 0) return
        const items = [...pendingRef.current]
        const currentFile = fileRef.current
        if (currentFile) {
          const name =
            documentNameRef.current.trim() ||
            fileNameWithoutExtension(currentFile.name) ||
            currentFile.name
          items.push({ localId: 'current', file: currentFile, documentName: name })
        }
        if (items.length === 0) return

        for (const item of items) {
          await uploadOne(item.file, item.documentName, rowId)
        }
        setPending([])
        clearPicker()
        await loadDocuments()
      },
    }))

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t('entityDocuments.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
            <div className="space-y-2">
              <Label htmlFor="entity-document-file">{t('entityDocuments.file')}</Label>
              <Input
                id="entity-document-file"
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt"
                onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="entity-document-name">{t('entityDocuments.documentName')}</Label>
              <Input
                id="entity-document-name"
                value={documentName}
                onChange={(e) => setDocumentName(e.target.value)}
                placeholder={t('entityDocuments.enterDocumentName')}
              />
            </div>
            <Button type="button" onClick={handleUpload} disabled={uploading || !file}>
              {uploading ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  {t('entityDocuments.uploading')}
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  {t('entityDocuments.upload')}
                </>
              )}
            </Button>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('entityDocuments.documentName')}</TableHead>
                  <TableHead>{t('entityDocuments.fileName')}</TableHead>
                  <TableHead>{t('entityDocuments.createdDate')}</TableHead>
                  <TableHead className="text-right">{t('entityDocuments.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      {t('entityDocuments.loading')}
                    </TableCell>
                  </TableRow>
                ) : pending.length === 0 && documents.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                      {t('entityDocuments.noDocuments')}
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {pending.map((item) => (
                        <TableRow key={item.localId}>
                          <TableCell className="font-medium">{item.documentName || '—'}</TableCell>
                          <TableCell>{item.file.name}</TableCell>
                          <TableCell className="text-muted-foreground">{t('entityDocuments.pending')}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive"
                                onClick={() => handleDeletePending(item)}
                                title={t('entityDocuments.delete')}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                    ))}
                    {documents.map((doc) => {
                      const href = resolveDocumentPath(doc.documentPath || doc.path)
                      return (
                        <TableRow key={doc.id}>
                          <TableCell className="font-medium">{doc.documentName || '—'}</TableCell>
                          <TableCell>{doc.originalFileName || '—'}</TableCell>
                          <TableCell>{formatDate(doc.createdDate)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              {href ? (
                                <Button type="button" variant="ghost" size="icon" className="h-8 w-8" asChild>
                                  <a href={href} target="_blank" rel="noopener noreferrer" title={t('entityDocuments.download')}>
                                    <Download className="h-4 w-4" />
                                  </a>
                                </Button>
                              ) : null}
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive"
                                disabled={deletingId === doc.id}
                                onClick={() => handleDeleteSaved(doc)}
                                title={t('entityDocuments.delete')}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    )
  }
)

EntityDocuments.displayName = 'EntityDocuments'
